
import React, { useState } from 'react';
import { Menu, Search, RefreshCw, Globe, Plus } from "lucide-react";
import { useAuth } from "@/contexts/auth";
import { usePermissions } from "@/hooks/use-permissions";
import { useIsMobile } from "@/hooks/use-mobile";
import AdminSidebar from "@/components/admin/AdminSidebar";
import AdminHeader from "@/components/admin/AdminHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSitemapManagement } from "@/hooks/admin/sitemap/useSitemapManagement";
import { SitemapStats } from "@/components/admin/sitemap/SitemapStats";
import { SitemapEntriesTable } from "@/components/admin/sitemap/SitemapEntriesTable";
import { SitemapPreview } from "@/components/admin/sitemap/SitemapPreview";
import { SitemapGenerationLogs } from "@/components/admin/sitemap/SitemapGenerationLogs";
import { AddStaticEntryForm } from "@/components/admin/sitemap/AddStaticEntryForm";
import { StaticPageDiscovery } from "@/components/admin/sitemap/StaticPageDiscovery";
import { useEffect } from "react";

const AdminSitemap = () => {
  const { user, profile, isLoading } = useAuth();
  const { isSuperAdmin, isAdmin } = usePermissions();
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  
  const {
    entries,
    logs,
    stats,
    entriesLoading,
    logsLoading,
    statsLoading,
    searchTerm,
    setSearchTerm,
    filterType,
    setFilterType,
    isGenerating,
    generateDynamicEntries,
    toggleEntry,
    deleteEntry,
    addStaticEntry,
    generateSitemapXML,
  } = useSitemapManagement();
  
  useEffect(() => {
    document.title = "Sitemap Management - NationCard";
  }, []);
  
  // Effect to handle sidebar state on mobile/desktop switch
  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);
  
  if (isLoading) {
    return <div className="flex h-screen w-full items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-nation-blue mx-auto mb-4"></div>
        <p className="text-nation-blue">Loading sitemap management...</p>
      </div>
    </div>;
  }
  
  // Additional check to ensure we have a user with proper permissions before rendering
  if (!user || (!isAdmin() && !isSuperAdmin())) {
    return null;
  }
  
  return (
    <div className="flex min-h-screen bg-nation-purple/10 dark:bg-gray-900/90">
      {/* Mobile sidebar toggle */}
      {isMobile && !sidebarOpen && (
        <Button
          variant="outline"
          size="icon"
          className="fixed top-4 left-4 z-40 md:hidden"
          onClick={() => setSidebarOpen(true)}
        >
          <Menu className="h-4 w-4" />
        </Button>
      )}
      
      {/* Sidebar with conditional display for mobile */}
      <div className={`${sidebarOpen ? "block" : "hidden"} ${isMobile ? "fixed inset-0 z-50 bg-black/50" : ""}`} onClick={isMobile ? () => setSidebarOpen(false) : undefined}>
        <div onClick={e => isMobile && e.stopPropagation()} className={isMobile ? "h-full w-64" : ""}>
          <AdminSidebar onClose={isMobile ? () => setSidebarOpen(false) : undefined} />
        </div>
      </div>
      
      <div className="flex-1">
        <AdminHeader>
          {isMobile && (
            <Button
              variant="outline"
              size="icon"
              className="mr-2"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-4 w-4" />
            </Button>
          )}
          Sitemap Management
        </AdminHeader>
        
        <main className="p-4 md:p-6 space-y-4 md:space-y-6">
          <div className="max-w-7xl mx-auto">
            {/* Statistics Overview */}
            <SitemapStats stats={stats} isLoading={statsLoading} />
            
            {/* Quick Actions */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <Button
                onClick={generateDynamicEntries}
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Generate Dynamic Entries
              </Button>
              
              <Button
                variant="outline"
                onClick={() => window.open('/sitemap.xml', '_blank')}
                className="flex items-center gap-2"
              >
                <Globe className="h-4 w-4" />
                View Current Sitemap
              </Button>
            </div>

            {/* Main Content Tabs */}
            <Tabs defaultValue="entries" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="entries">Sitemap Entries</TabsTrigger>
                <TabsTrigger value="discovery">Page Discovery</TabsTrigger>
                <TabsTrigger value="preview">Preview & Generate</TabsTrigger>
                <TabsTrigger value="logs">Generation Logs</TabsTrigger>
              </TabsList>
              
              <TabsContent value="entries" className="space-y-4">
                {/* Add Static Entry Form */}
                <AddStaticEntryForm
                  onAddEntry={async (entry) => {
                    await addStaticEntry(entry);
                  }}
                  isLoading={false}
                />
                
                {/* Search and Filter Controls */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      placeholder="Search URLs..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  
                  <Select value={filterType} onValueChange={(value: any) => setFilterType(value)}>
                    <SelectTrigger className="w-full sm:w-48">
                      <SelectValue placeholder="Filter by type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="static">Static</SelectItem>
                      <SelectItem value="dynamic">Dynamic</SelectItem>
                      <SelectItem value="generated">Generated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Entries Table */}
                <SitemapEntriesTable
                  entries={entries}
                  isLoading={entriesLoading}
                  onToggleEntry={toggleEntry}
                  onDeleteEntry={deleteEntry}
                />
              </TabsContent>
              
              <TabsContent value="discovery">
                <StaticPageDiscovery 
                  onAddEntries={async (entries) => {
                    for (const entry of entries) {
                      await addStaticEntry(entry);
                    }
                  }}
                  existingEntries={entries}
                />
              </TabsContent>
              
              <TabsContent value="preview">
                <SitemapPreview
                  onGenerateXML={generateSitemapXML}
                  isGenerating={isGenerating}
                />
              </TabsContent>
              
              <TabsContent value="logs">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium">Generation History</h3>
                    <p className="text-sm text-muted-foreground">
                      Track all sitemap generation activities and performance metrics
                    </p>
                  </div>
                  
                  <SitemapGenerationLogs
                    logs={logs}
                    isLoading={logsLoading}
                  />
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminSitemap;
